import { useDropzone } from 'react-dropzone';
import { Box, InputLabel, Typography, useTheme } from '@mui/material';
import { UploadIcon } from 'components/Icons/UploadIcon';
import hexToRgba from 'hex-to-rgba';
import { ToastType, useToast } from 'services/toast';

type UploadBoxProps = {
  text?: string;
  acceptedFiles: string[];
  onUpload: (files: File[]) => void;
  maxFiles?: number;
  onError?: (error: Error) => void;
};

const UploadBox: React.FC<UploadBoxProps> = ({
  text,
  acceptedFiles,
  onUpload,
  maxFiles,
  onError
}) => {
  const { showToast } = useToast();

  const {
    palette: { info, primary }
  } = useTheme();

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onError: (error) => {
      onError?.(error);
    },
    onDrop: (acceptedFiles) => {
      onUpload(acceptedFiles);
    },
    onDropRejected: (rejectedFiles) => {
      showToast({
        message: rejectedFiles[0].errors[0].message,
        type: ToastType.Error
      });
    },
    noClick: true,
    noKeyboard: true,
    accept: acceptedFiles.reduce((acc, i) => ({ ...acc, [i]: [] }), {}),
    maxFiles: maxFiles
  });

  return (
    <Box>
      <InputLabel>Upload file</InputLabel>
      <Box
        {...getRootProps()}
        sx={{
          border: `1px solid ${info.main}`,
          padding: 6,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive
            ? hexToRgba(info.main, 0.7)
            : 'transparent'
        }}
        onClick={open}
      >
        <input {...getInputProps()} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <UploadIcon />
        </Box>
        <Typography variant="body2">
          <span
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: primary.main
            }}
          >
            Click to upload
          </span>{' '}
          or drag and drop
        </Typography>
        {text && <Typography variant="body2">{text}</Typography>}
      </Box>
    </Box>
  );
};

export { UploadBox };
